import { Box, Chip, lighten, useMediaQuery } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import {
    useGetJobsQuery,
    useGetProfileCompanyInfoQuery,
} from '../../../../app/Slices/SevenDayApiSlice';
import { Job, ProviderType } from '../../../../app/Slices/SevenDayApiTypes';
import { camelCaseToTitleCase } from '../../../../shared/utils';
import { MRT_ColumnDef } from 'mantine-react-table';
import GenericTable from '../../../shared/GenericTable';
import UserRole from '../../../../shared/AuthRoles';
import { ContractorRole } from '../../../enums/users';
import { theme } from '../../../../shared/theme';
import { useSearchParams } from 'react-router-dom';
import JobPreview from '../Owner/JobPreview';
import { BookingSetMenu } from '../../../types/set-menu';
import { CustomChip } from '../../../../layout/styledcomponents/CustomChip';

export type Status =
    | 'pending'
    | 'accepted'
    | 'declined'
    | 'cancelled'
    | 'completed'
    | undefined;

interface JobListProps {
    status?: Status;
    booking?: any;
    onSelectJob?: (job: Job | BookingSetMenu) => void;
}

const isBookingSetMenu = (row: Job | BookingSetMenu): row is BookingSetMenu =>
    row.providerType === ProviderType.Owner;

const JobList = ({ status, booking, onSelectJob }: JobListProps) => {
    const loggedInUser = UserRole();
    const bookingId = booking?.id;
    const { data: jobs } = useGetJobsQuery({
        bookingId,
        ...(status ? { status } : {}),
    });

    const isOwner = loggedInUser?.role.includes('owner');
    const { data: companyDetails } = useGetProfileCompanyInfoQuery(
        `owners/${loggedInUser?.company}`,
        { skip: !isOwner },
    );

    const allData: (Job | BookingSetMenu)[] = [
        ...(jobs ?? []),
        ...(booking?.setMenus ?? []),
    ];

    const isMobile = useMediaQuery('(max-width: 770px)');

    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedJob, setSelectedJob] = useState<number | null>(null);
    const [jobOpen, setJobOpen] = useState(false);

    const visibleJobIds = allData.map((job) => ({
        id: job.id as number,
        providerType: job.providerType,
    }));

    const providerTypeParam = searchParams.get('provider-type');

    const selectedJobIdFromUrl = useMemo(() => {
        const idParam = searchParams.get('selectedJob');
        if (!idParam) return null;
        return Number(idParam);
    }, [searchParams]);

    const initialIndex = visibleJobIds.findIndex(
        (job) =>
            job.id === selectedJobIdFromUrl &&
            (providerTypeParam ? job.providerType === providerTypeParam : true),
    );

    useEffect(() => {
        const id = searchParams.get('selectedJob');
        if (!id) {
            setJobOpen(false);
            setSelectedJob(null);
            return;
        }

        const parsed = Number(id);
        if (!Number.isFinite(parsed)) return;

        setSelectedJob(parsed);
        setJobOpen(true);
    }, [searchParams]);

    const getStatusSx = (s: string) => {
        switch (s) {
            case 'approved':
            case 'active':
                return {
                    bgcolor: lighten(theme.palette.yellow.main, 0.78),
                    color: theme.palette.dark.main,
                    borderColor: lighten(theme.palette.yellow.main, 0.35),
                };
            case 'pending':
            case 'pendingBooking':
                return {
                    bgcolor: lighten(theme.palette.primary.main, 0.76),
                    color: theme.palette.dark.main,
                    borderColor: lighten(theme.palette.primary.main, 0.32),
                };
            case 'cancelled':
            case 'declined':
                return {
                    bgcolor: lighten(theme.palette.error.main, 0.82),
                    color: theme.palette.dark.main,
                    borderColor: lighten(theme.palette.error.main, 0.3),
                };
            case 'completed':
                return {
                    bgcolor: lighten(theme.palette.info.main, 0.82),
                    color: theme.palette.dark.main,
                    borderColor: lighten(theme.palette.info.main, 0.3),
                };
            default:
                return {
                    bgcolor: theme.palette.custom.cardActionBackground,
                    color: theme.palette.text.primary,
                    borderColor: theme.palette.custom.lightBorderColor,
                };
        }
    };

    const handleView = (job: Job | BookingSetMenu) => {
        if (job.id == null) return;

        if (onSelectJob) {
            onSelectJob(job);
            return;
        }

        setSelectedJob(job.id);
        setJobOpen(true);

        const next = new URLSearchParams(searchParams);
        next.set('selectedJob', String(job.id));
        next.set('provider-type', String(job.providerType));
        setSearchParams(next);
    };

    const handleClose = () => {
        setJobOpen(false);
        setSelectedJob(null);

        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('selectedJob');
            next.delete('provider-type');
            return next;
        });
    };

    const columns: MRT_ColumnDef<any>[] = useMemo(() => {
        const cols: MRT_ColumnDef<any>[] = [
            {
                accessorKey: 'id',
                header: 'Job Id',
                size: 60,
                minSize: 50,
                maxSize: 70,
                Cell: ({ cell }) => `#${cell.getValue()}`,
            },
            {
                accessorKey: 'providerType',
                header: 'Provider',
                accessorFn: (row) =>
                    row?.providerType === ProviderType.Contractor
                        ? 'Contractor'
                        : `${companyDetails?.name ?? 'Owner'} Services`,
            },
            {
                accessorKey: 'contractor.name',
                header: 'Contractor',
                accessorFn: (row: Job | BookingSetMenu) =>
                    isBookingSetMenu(row) ? '-' : row.contractor?.name,
            },
        ];

        if (loggedInUser?.role === ContractorRole.Admin) {
            cols.push({
                accessorKey: 'assignee.firstName',
                header: 'Assignee',
                accessorFn: (row: Job | BookingSetMenu) =>
                    isBookingSetMenu(row)
                        ? '-'
                        : row.assignee?.firstName
                          ? `${row.assignee.firstName} ${row.assignee.lastName}`
                          : row?.assignee?.email,
            });
        }

        cols.push(
            {
                accessorKey: 'jobDate',
                header: 'Due Date',
                size: 60,
                minSize: 50,
                maxSize: 70,
                Cell: ({ cell }) =>
                    new Date(cell.getValue<string>()).toLocaleDateString(
                        'en-AU',
                    ),
            },
            {
                accessorKey: 'startTime',
                header: 'Start',
                size: 60,
                minSize: 50,
                maxSize: 70,
            },
            {
                accessorKey: 'endTime',
                header: 'End',
                size: 60,
                minSize: 50,
                maxSize: 70,
            },
            {
                accessorKey: 'services',
                header: 'Services',
                Cell: ({ cell, row }) => {
                    const value = cell.getValue<any[]>() ?? [];
                    const isOwnerJob = isBookingSetMenu(row.original);
                    const ownerJob = isOwnerJob
                        ? `${row.original.serviceType} set menu`
                        : undefined;
                    const remainders = isMobile
                        ? isOwnerJob
                            ? 0
                            : value.length
                        : isOwnerJob
                          ? 0
                          : value.length - 2;

                    return (
                        <Box
                            sx={{ display: 'flex', overflow: 'clip', gap: 0.5 }}
                        >
                            {isOwnerJob ? (
                                <CustomChip
                                    variant='filled'
                                    sx={{
                                        backgroundColor: 'lightgrey',
                                    }}
                                    label={camelCaseToTitleCase(ownerJob ?? '')}
                                />
                            ) : (
                                !isMobile &&
                                value
                                    .slice(0, 2)
                                    .map((service: string, index: number) => (
                                        <Chip
                                            key={index}
                                            sx={{
                                                height: 'auto',
                                                fontSize: '10px',
                                            }}
                                            label={camelCaseToTitleCase(
                                                service,
                                            ).toUpperCase()}
                                        />
                                    ))
                            )}
                            {remainders > 0 && (
                                <Chip
                                    sx={{ height: 'auto', fontSize: '10px' }}
                                    label={`+ ${remainders}`}
                                />
                            )}
                        </Box>
                    );
                },
            },
            {
                accessorKey: 'status',
                header: 'Status',
                Cell: ({ cell }) => {
                    const value = cell.getValue<string>() ?? '';
                    return (
                        <Chip
                            label={camelCaseToTitleCase(value).toUpperCase()}
                            sx={{
                                ...getStatusSx(value),
                                height: 'auto',
                                fontSize: '10px',
                                border: '1px solid',
                            }}
                        />
                    );
                },
            },
        );

        return cols;
    }, [isMobile, loggedInUser?.role, companyDetails?.name]);

    return (
        <Box sx={{ width: '100%', paddingTop: '20px' }}>
            <GenericTable
                columns={columns}
                data={allData}
                tableName='Job-List'
                loading={jobs === undefined && !booking?.setMenus?.length}
                onRowClick={(row) => handleView(row)}
                cursor='pointer'
                enableRowSelection={false}
                enableMultiRowSelection={false}
                enableSelectAll={false}
            />

            <JobPreview
                id={selectedJobIdFromUrl ?? selectedJob}
                open={jobOpen}
                jobIds={visibleJobIds}
                initialIndex={initialIndex}
                onClose={handleClose}
            />
        </Box>
    );
};

export default JobList;
